import React, {useEffect, useState} from 'react'
import {Box, Button, IconButton, Tooltip, Typography} from "@material-ui/core";
import {useSnackbar} from "notistack";
import MomentUtils from "@date-io/moment";
import 'moment/locale/es';
import SubHeader from "components/helpers/subHeader/subHeader";
import {ReactGrid} from "components/helpers/reactGrid/reactGrid";
import {DateRangeControl} from "components/helpers/dateRange/dateRange.control";
import {useThalamusService} from "services/hooks";
import {downloadFile} from "assets/utils";
import CheckCircleRounded from "@material-ui/icons/CheckCircleRounded";
import HighlightOffRounded from "@material-ui/icons/HighlightOffRounded";
import ReportProblemRounded from "@material-ui/icons/ReportProblemRounded";
import ScheduleRounded from "@material-ui/icons/ScheduleRounded";
import RefreshRounded from "@material-ui/icons/RefreshRounded";

const COLUMNS = [
    {name: 'fecha', title: 'Fecha'},
    {name: 'dia', title: 'Día'},
    {name: 'custom1', title: 'Archivo Ventas'},
    {name: 'custom2', title: 'Archivo Clientes'}
];

// Resultado del procesamiento del archivo en Thalamus (el prefijo del nombre lo indica).
const ESTADOS = {
    procesado: {texto: 'Enviado', detalle: 'Procesado', color: '#43a047', icono: CheckCircleRounded},
    error: {texto: 'Error al procesar', detalle: 'Falló el', color: '#fb8c00', icono: ReportProblemRounded},
    pendiente: {texto: 'Pendiente', detalle: 'Enviado', color: '#757575', icono: ScheduleRounded}
};

// Muestra el tamaño del archivo en KB (los excel de Thalamus son siempre chicos).
const formatTamanio = tamanio => tamanio ? `${Math.max(1, Math.round(tamanio / 1024))} KB` : '';

// Tope de días por descarga múltiple: cada archivo tarda cerca de un segundo en bajar del FTP.
const MAX_DIAS = 31;

export const Thalamus = () => {
    const [desde, setDesde] = useState((new MomentUtils()).date(new Date()).startOf('month'));
    const [hasta, setHasta] = useState((new MomentUtils()).date(new Date()));
    const [dias, setDias] = useState([]);
    const [seleccionados, setSeleccionados] = useState([]);

    const {enqueueSnackbar} = useSnackbar();
    const ThalamusService = useThalamusService();

    const getDias = () => {
        setSeleccionados([]);
        ThalamusService.getArchivos(
            desde.format('DD/MM/YYYY'),
            hasta.format('DD/MM/YYYY'),
            response => setDias(response.data.result),
            error => {
                setDias([]);
                enqueueSnackbar(error, {variant: 'error'});
            }
        );
    };

    useEffect(() => {
        getDias();
    }, [desde, hasta]);

    // Archivos existentes de los días tildados (un día puede tener uno solo de los dos, o ninguno).
    const rutasSeleccionadas = () =>
        dias
            .filter(dia => seleccionados.indexOf(dia.id) !== -1)
            .reduce((rutas, dia) => {
                if (dia.ventas.enviado) rutas.push(dia.ventas.ruta);
                if (dia.clientes.enviado) rutas.push(dia.clientes.ruta);
                return rutas;
            }, []);

    const descargarLote = formato => {
        if (seleccionados.length > MAX_DIAS) {
            enqueueSnackbar(`Solo puede descargar hasta ${MAX_DIAS} días por vez. Acote la selección.`, {variant: 'warning'});
            return;
        }

        const rutas = rutasSeleccionadas();

        if (rutas.length === 0) {
            enqueueSnackbar('Los días seleccionados no tienen archivos para descargar.', {variant: 'warning'});
            return;
        }

        ThalamusService.descargarArchivos(
            formato,
            rutas,
            (file, filename) => downloadFile(file, filename || `Thalamus.${formato === 'zip' ? 'zip' : 'xlsx'}`),
            error => enqueueSnackbar(error, {variant: 'error'})
        );
    };

    const ArchivoCell = ({archivo, esperado}) => {
        if (!archivo || !archivo.enviado)
            return (
                <Box className={'d-flex align-items-center justify-content-center'}>
                    {/* El archivo de un día se envía a la madrugada siguiente: hasta entonces no falta. */}
                    {esperado === false ? (
                        <>
                            <ScheduleRounded style={{color: '#757575'}} fontSize={"small"}/>
                            <Typography variant={"body2"} className={'ml-1'}>Aún no corresponde</Typography>
                        </>
                    ) : (
                        <>
                            <HighlightOffRounded style={{color: '#e53935'}} fontSize={"small"}/>
                            <Typography variant={"body2"} className={'ml-1'}>No enviado</Typography>
                        </>
                    )}
                </Box>
            );

        const estado = ESTADOS[archivo.estado] || ESTADOS.pendiente;
        const Icono = estado.icono;

        const detalle = [
            archivo.nombre,
            archivo.procesado ? `${estado.detalle} el ${archivo.procesado}` : '',
            formatTamanio(archivo.tamanio)
        ].filter(parte => parte !== '').join(' — ');

        return (
            <Tooltip title={detalle}>
                <Box className={'d-flex align-items-center justify-content-center'}>
                    <Icono style={{color: estado.color}} fontSize={"small"}/>
                    <Typography variant={"body2"} className={'ml-1'}>{estado.texto}</Typography>
                </Box>
            </Tooltip>
        );
    };

    const columnExtensions = [
        {columnName: 'fecha', width: 140, align: 'center'},
        {columnName: 'dia', width: 140},
        {columnName: 'custom1', component: ({row}) => <ArchivoCell archivo={row.ventas} esperado={row.esperado}/>, align: 'center'},
        {columnName: 'custom2', component: ({row}) => <ArchivoCell archivo={row.clientes} esperado={row.esperado}/>, align: 'center'}
    ];

    return (
        <Box className='niquel-thalamus'>
            <SubHeader
                title={'Thalamus'}
                subtitle={'Envío diario de archivos de ventas y clientes'}
            />
            <Box className='container-xl mt-3 mb-3'>
                <Box className='wrapper'>
                    <ReactGrid
                        title={'Archivos enviados por período'}
                        height={-230}
                        columns={COLUMNS}
                        columnExtensions={columnExtensions}
                        rows={dias}
                        showResultCount
                        enableFiltering={false}
                        enableSorting={false}
                        enableSearch={false}
                        enableSelection
                        getRowId={row => row.id}
                        selectedRows={seleccionados}
                        onSelectionChange={setSeleccionados}
                        onDelete={() => setSeleccionados([])}
                        customBtn1={
                            <DateRangeControl
                                className={'ml-2'}
                                desde={desde}
                                hasta={hasta}
                                onChange={(nuevoDesde, nuevoHasta) => {
                                    setDesde(nuevoDesde);
                                    setHasta(nuevoHasta);
                                }}
                            />
                        }
                        customBtn2={
                            <Box className={'d-flex align-items-center'}>
                                <Tooltip title={'Descargar archivos en zip'}>
                                    <span>
                                        <Button
                                            color="primary"
                                            variant='outlined'
                                            size={"small"}
                                            className='ml-2'
                                            disabled={seleccionados.length === 0}
                                            onClick={() => descargarLote('zip')}
                                        >
                                            Descargar archivos
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={'Descargar en un solo excel'}>
                                    <span>
                                        <Button
                                            color="primary"
                                            variant='outlined'
                                            size={"small"}
                                            className='ml-2'
                                            disabled={seleccionados.length === 0}
                                            onClick={() => descargarLote('excel')}
                                        >
                                            Excel unificado
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={'Actualizar'}>
                                    <IconButton onClick={getDias}>
                                        <RefreshRounded/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    />
                </Box>
            </Box>
        </Box>
    )
};
